import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Building } from "lucide-react";
import GenericBox from "components/ui/Box";
import { Sizes } from "enums/sizes";
import { usePanel } from "hooks/usePanel";
import { useCoproperty } from "features/subscription-process/coproperty/useCoproperty";
import { CopropertySummaryPanel } from "./CopropertySummaryPanel";

/** Surface en m² au format français ("2 500 m²"). */
const formatArea = (value: number): string => `${Math.round(value).toLocaleString("fr-FR")} m²`;

/**
 * Carte « La copropriété » de l'en-tête du tarificateur MRI : trois lignes, comme les autres
 * cartes d'entité. Le crayon ouvre le panneau récapitulatif, en consultation seule : la
 * modification de la copropriété se fait à la contractualisation.
 */
export const InsuredCopropertyCard: React.FC = () => {
  const navigate = useNavigate();
  const { openPanel, closePanel } = usePanel();
  const { riskId, coproperty } = useCoproperty();

  const onSelectCard = useCallback(() => {
    if (!coproperty || !riskId) return;
    openPanel(
      { title: "La copropriété", size: Sizes["2xl"] },
      <CopropertySummaryPanel
        coproperty={coproperty}
        riskId={riskId}
        readOnly
        onNavigate={(route) => {
          closePanel();
          navigate(route);
        }}
      />
    );
  }, [coproperty, riskId, openPanel, closePanel, navigate]);

  if (!coproperty) {
    return null;
  }

  const address = coproperty.address?.adresseFormattee || coproperty.address?.ligne1 || null;
  const buildingsCount = coproperty.buildings?.length ?? 0;

  return (
    <GenericBox icon={Building} title="La copropriété" onSelect={onSelectCard}>
      {/* Deux lignes, comme la carte du bien : la hauteur de l'en-tête suit la carte la plus haute. */}
      <div className="flex flex-col gap-1 text-sm">
        <p className="truncate font-medium">{coproperty.name || address || "Copropriété"}</p>
        <p className="truncate text-gray-500">
          {[
            address,
            coproperty.retainedArea != null && formatArea(coproperty.retainedArea),
            buildingsCount > 0 && `${buildingsCount} bâtiment${buildingsCount > 1 ? "s" : ""}`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
    </GenericBox>
  );
};
